import React, { useState } from 'react'
import { Formik, Form } from 'formik'
import { reportError } from 'src/sentry'

import i18n from 'src/i18n'
import { Trans, useTranslation } from 'react-i18next'
import { useStore, actions } from 'store'
import { Alert, AlertTitle } from '@material-ui/lab'
import { Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import { apiUrl } from 'src/config'
import localStorageWrapper from 'src/util/localStorageWrapper'
import analytics from 'src/util/analytics'
import navigate from 'src/util/navigate'

import {
  form as ContactDetails,
  initial as ContactDetailsInitial,
  schema as ContactDetailsSchema,
} from 'components/FormSteps/ContactDetails'
import { LoadingIndicator, Link, Button } from 'components'

const useStyles = makeStyles((theme) => ({
  cta: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
}))

const ExistsAlreadyAlert = () => (
  <Alert severity="warning" style={{ margin: '8px 0' }}>
    <Trans i18nKey="formErrors:existsAlready">
      <AlertTitle />
      <Link to="airbnbEmailLogin" />
    </Trans>
  </Alert>
)

const ExistsAlreadyOtherBookAlert = () => (
  <Alert severity="warning" style={{ margin: '8px 0' }}>
    <Trans i18nKey="formErrors:existsAlreadyOtherBook">
      <AlertTitle />
    </Trans>
  </Alert>
)

const ProbablyAlreadyHasAnAccountAlert = () => (
  <Alert severity="warning" style={{ margin: '8px 0' }}>
    <Trans i18nKey="formErrors:probablyAlreadyHasAnAccount">
      <AlertTitle />
      <Link to="airbnbEmailLogin" />
    </Trans>
  </Alert>
)

const airbnbDataToInitial = (airbnbData) => {
  const houseNumberRaw = airbnbData.contact?.currentProperty?.houseNumber
  let houseNumberInput, houseNumberAddition
  if (houseNumberRaw) {
    const matches = houseNumberRaw.match(/^(\d*)-?(.*)/)
    if (matches) {
      houseNumberInput = matches[1]
      houseNumberAddition = matches[2]
    }
  }

  const data = {
    fullName: airbnbData.contact.fullName,
    email: airbnbData.contact.email,
    phone: airbnbData.contact.phone,
    houseNumberInput,
    houseNumberAddition,
    street: airbnbData.contact?.currentProperty?.street,
    zipcode: airbnbData.contact?.currentProperty?.zipcode,
    allowedPrivacy: !!airbnbData.contact.allowedPrivacy,
    allowedGeneralTerms: !!airbnbData.contact.allowedGeneralTerms,
    allowedProxy: !!airbnbData.allowedProxy,
    allowedAgreement: !!airbnbData.allowedAgreement,
  }
  Object.entries(data).forEach(([key, value]) => {
    if (!value) {
      data[key] = ''
    }
  })
  return data
}

const AirbnbForm = ({ anonymousId }) => {
  const classes = useStyles()

  const { t } = useTranslation()
  const [serverError, setServerError] = useState(false)
  const [
    {
      auth,
      airbnbData,
      airbnbDataLoaded,
      airbnbDataLoading,
      airbnbDataError,
      referralCode,
      promoCode,
      utm_source,
      loginEmail,
    },
    dispatch,
  ] = useStore()

  const [initialKey, setInitialKey] = useState(false)
  const [navigating, setNavigating] = useState(false)

  if (airbnbDataError) {
    // if the user does not exist yet, no problem
    if (airbnbDataError.statusCode !== 404) {
      // all other errors are a problem :(
      return <span>{airbnbDataError.message}</span>
    }
  }

  if (auth.isAuthenticated) {
    if (!airbnbDataLoaded && !airbnbDataError) {
      if (!airbnbDataLoading) {
        dispatch(actions.fetchAirbnbData())
      }
      return <LoadingIndicator />
    }
  }

  // reload the page if the logged in user changed or logged out
  if (airbnbData && !initialKey) {
    setInitialKey(airbnbData.key)
  }

  // reload the page if the logged in user changed or logged out
  if (airbnbData && airbnbData.completed) {
    return (
      <Alert severity="info" style={{ margin: '8px 0' }}>
        <Trans i18nKey="formMessages:received">
          <AlertTitle />
          <Link href="#" onClick={() => window.location.reload()} />
        </Trans>
      </Alert>
    )
  }

  if (initialKey && !navigating && initialKey !== airbnbData.key) {
    // we already had an initial key, reset the form
    window.location.reload()
    return null
  }

  if (auth.isAuthenticated && airbnbData.stateSystemKey !== 'incomplete') {
    // form is already completed
    return <ExistsAlreadyAlert />
  }

  const initialValues = airbnbData
    ? airbnbDataToInitial(airbnbData)
    : ContactDetailsInitial

  return (
    <>
      {loginEmail && !auth.isAuthenticated && __BUILDFOR__ === ' ' && (
        <ProbablyAlreadyHasAnAccountAlert />
      )}

      <Formik
        initialValues={initialValues}
        validationSchema={ContactDetailsSchema(promoCode)}
        validateOnMount={false}
        onSubmit={async (data, _bag) => {
          setServerError(false)
          const payload = { ...data, allowedPrivacy: true }

          // if we have saved a source from utm, send it to the server
          if (utm_source) {
            payload.source = utm_source
          }

          // add language to the first request
          payload.language = i18n.language
          if (referralCode) {
            payload.referralCode = referralCode
          }
          if (promoCode) {
            payload.promoCode = promoCode
            payload.source = promoCode
          }
          payload.book = __BUILDFOR__
          if (anonymousId) {
            payload.anonymousId = anonymousId
          }

          const headers = {
            'Content-Type': 'application/json',
          }
          if (auth.isAuthenticated && auth.user) {
            const token = await auth.user.getIdToken()
            headers.authorization = `Bearer ${token}`
          }

          const response = await fetch(`${apiUrl}/airbnbForm/contactDetails`, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers,
            credentials: 'include',
            referrerPolicy: 'no-referrer-when-downgrade',
          }).then((r) => r.json())

          switch (response.statusCode) {
            case 409:
              if (auth.isAuthenticated) {
                setServerError(
                  <Alert severity="warning" style={{ margin: '8px 0' }}>
                    Telefoonnummer bestaat al
                  </Alert>,
                )
                return false
              }
              if (response.type === 'book_own') {
                setServerError(<ExistsAlreadyAlert />)
              } else {
                setServerError(<ExistsAlreadyOtherBookAlert />)
              }
              return false

            case 200:
              analytics.fireEvent(`formstep-contactDetails-submit-success`)
              break

            default:
              analytics.fireEvent(`formstep-contactDetails-submit-error`, {
                message: response.message,
              })
              if (response.message) {
                reportError(response.message)
                setServerError(response.message)
              }
              return false
          }

          // save the airbnb key so we can send it with all further requests
          if (!auth.isAuthenticated) {
            dispatch(
              actions.setAirbnbDataFromForm({
                key: response.result.key,
                loginKey: response.result.loginKey,
                activeReferralLink: response.result.activeReferralLink,
                ...payload,
              }),
            )
          } else {
            dispatch(actions.fetchAirbnbData())
          }

          // identify this user with segment
          analytics.identify(response.result.firebaseUid)

          const loginEmail = payload.email
          localStorageWrapper.setItem('loginEmail', loginEmail)
          dispatch(
            actions.setLoginInfo({
              loginEmail,
            }),
          )
          analytics.fireEvent('airbnb/signUp-jair', {
            order_id: response.result.key,
          })

          if (__BUILDFOR__ === 'stichting') {
            // skip adding bookings
            navigate('airbnbConfirmEmail')
          } else {
            if (auth.isAuthenticated) {
              navigate('airbnbAddBooking')
            } else {
              navigate('airbnbAddBookingNotLoggedIn')
            }
          }
          setNavigating(true)

          // keep the form loading while we go to the new page
          await new Promise(() => {})
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <ContactDetails
              promoCode={promoCode}
              isAuthenticated={auth.isAuthenticated}
            />
            {serverError && (
              <Typography color="error">{serverError}</Typography>
            )}
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
              className={classes.cta}
              fullWidth
            >
              {t('buttons:send')}
            </Button>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default AirbnbForm
